// Document drafts prepared by Zoki.
//
// The assistant cannot render a PDF (that happens in this browser), so it
// records what it would have generated and the makler turns it into a real
// document with one click. See apps/api/migrations/0056_document_drafts.sql.

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiFetch } from '@/lib/api';

export interface DocumentDraftRow {
  id: string;
  type: 'vermittlungsauftrag' | 'besichtigungsschein' | 'kaufanbot';
  appointment_id: string | null;
  client_id: string | null;
  property_id: string | null;
  offer_id: string | null;
  metadata: Record<string, unknown> | null;
  summary: string | null;
  requested_by: string;
  status: 'pending' | 'consumed' | 'dismissed';
  created_at: number | string;
}

/**
 * `document_drafts.metadata` is a Drizzle `mode: 'json'` column, so the server
 * hands it to `snakeCaseResponse` as a real OBJECT — and `toSnake` recurses,
 * rewriting the generator options Zoki wrote (`contractVariant`,
 * `exclusivityMonths`, `provisionNetPercent`) into snake_case on the wire.
 * `documents.metadata` is a plain text column, so its JSON string passes
 * through untouched; that asymmetry is why one path works and this one did
 * not. Consumers expect the camelCase shape the generator dialog uses, so the
 * options are converted back exactly once, here.
 *
 * Without this the assistant's choices were read as `undefined` and silently
 * replaced by the dialog defaults — including a prepared ORDINARY order
 * opening as an exclusive one.
 */
function normaliseDraft(draft: DocumentDraftRow): DocumentDraftRow {
  const va = (draft.metadata?.vermittlungsauftrag ?? null) as Record<string, unknown> | null;
  if (!va) return draft;
  const pick = (camel: string, snake: string) => va[camel] ?? va[snake];
  const normalised: Record<string, unknown> = { ...va };
  for (const [camel, snake] of [
    ['contractVariant', 'contract_variant'],
    ['exclusivityMonths', 'exclusivity_months'],
    ['provisionNetPercent', 'provision_net_percent'],
  ] as Array<[string, string]>) {
    const value = pick(camel, snake);
    if (value !== undefined) normalised[camel] = value;
  }
  return { ...draft, metadata: { ...draft.metadata, vermittlungsauftrag: normalised } };
}

export function useDocumentDrafts(params: { appointmentId?: string; clientId?: string }) {
  const qs = new URLSearchParams();
  if (params.appointmentId) qs.set('appointment_id', params.appointmentId);
  if (params.clientId) qs.set('client_id', params.clientId);
  const key = qs.toString();

  return useQuery({
    queryKey: ['document-drafts', key],
    // Only fetch when we actually have something to scope by — an unscoped
    // call would pull every pending draft in the company onto one screen.
    enabled: Boolean(params.appointmentId || params.clientId),
    queryFn: async (): Promise<DocumentDraftRow[]> => {
      const res = await apiFetch(`/documents/drafts?${key}`);
      if (!res.ok) return [];
      const body = (await res.json()) as { items?: DocumentDraftRow[] };
      return (body.items ?? []).map(normaliseDraft);
    },
  });
}

export function useConsumeDocumentDraft() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { draftId: string; documentId: string }) => {
      const res = await apiFetch(`/documents/drafts/${input.draftId}/consume`, {
        method: 'POST',
        body: JSON.stringify({ document_id: input.documentId }),
      });
      if (!res.ok) throw new Error('draft_consume_failed');
      return res.json();
    },
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['document-drafts'] }),
  });
}

export function useDismissDocumentDraft() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (draftId: string) => {
      const res = await apiFetch(`/documents/drafts/${draftId}/dismiss`, { method: 'POST' });
      if (!res.ok) throw new Error('draft_dismiss_failed');
      return res.json();
    },
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['document-drafts'] }),
  });
}
